/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { Link, useRouter } from 'react-resource-router';

import { cssMap, jsx } from '@atlaskit/css';
import ButtonItem from '@atlaskit/menu/button-item';
import CustomItem from '@atlaskit/menu/custom-item';
import LinkItem from '@atlaskit/menu/link-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import BaseUrlRespectedLink from '@atlassian/company-hub-common/base-url-respected-link';
import { useHubContext } from '@atlassian/company-hub-common/utils';

import { AccountDetails } from '../account-details';
import messages from './messages';

const styles = cssMap({
	menu: {
		width: '234px',
	},
});

export const AvatarMenu = (): JSX.Element => {
	di(useHubContext, fg);

	const hubContext = useHubContext();
	const [{ match }] = useRouter();
	const { user, env, actions, renderAvatarMenuOptions, cloudUrl } = hubContext;
	const { isJiraAdmin } = user;
	const { helpCenterId, hoistedProjectKey } = env;
	const { id: pageId } = match.params;
	const { onLogoutClick } = actions;

	const { formatMessage } = useIntl();
	const shouldUseConsumerAvatarMenu = fg('enable-jsm-in-hubs');
	const consumerAvatarMenuContent =
		shouldUseConsumerAvatarMenu && typeof renderAvatarMenuOptions === 'function'
			? renderAvatarMenuOptions()
			: null;

	const csmEditorUrl = fg('csm_appification_standalone_navigation')
		? `${cloudUrl}/jira/customer-service-management/experiences/${hoistedProjectKey}/settings/${helpCenterId}/support-website/edit${pageId ? `/${pageId}` : ''}`
		: `${cloudUrl}/jira/helpcenter/projects/${hoistedProjectKey}/settings/${helpCenterId}/support-website/edit${pageId ? `/${pageId}` : ''}`;

	return (
		<div css={styles.menu}>
			<MenuGroup>
				<Section>
					<AccountDetails />
				</Section>
				{consumerAvatarMenuContent ||
					(isJiraAdmin && (
						<Section hasSeparator>
							<LinkItem
								href={csmEditorUrl}
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling
								className="cv-exclude-from-customisation"
							>
								{formatMessage(messages.editSite)}
							</LinkItem>
						</Section>
					))}
				<Section hasSeparator>
					<CustomItem
						component={
							(fg('jsm_help_extension_link_href_base_fix')
								? BaseUrlRespectedLink
								: Link) as typeof Link
						}
						to={`/user/profile`}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling
						className="cv-exclude-from-customisation"
					>
						{formatMessage(messages.profile)}
					</CustomItem>
					<ButtonItem onClick={onLogoutClick}>{formatMessage(messages.logout)}</ButtonItem>
				</Section>
			</MenuGroup>
		</div>
	);
};
